import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useAppStore } from "@/store/app-store";
import type { components } from "@/api/types";
import { trackEvent, AnalyticsEvent } from "@/analytics";

/**
 * SOP/SOG document pipeline (RES-57/58): upload → confirm → extract → review.
 *
 * Upload is the backend's two-phase presigned-PUT flow: POST a ticket (row is
 * created `pending_upload` + a presigned S3 URL comes back), PUT the bytes to
 * S3 with a RAW fetch — the presigned signature locks the Content-Type, and
 * the S3 endpoint must NOT receive our Authorization / X-RespAi headers — then
 * POST confirm so the server verifies the object really landed (head_object)
 * and flips the row to `uploaded`.
 *
 * Every queryKey carries the active department id so a respai_admin switching
 * tenants never sees another department's documents or rules from cache.
 */

export type SopDocument = components["schemas"]["SopDocumentRead"];
export type SopRule = components["schemas"]["SopRuleRead"];
export type SopRuleUpdate = components["schemas"]["SopRuleUpdate"];
export type SopExtractionRun = components["schemas"]["SopExtractionRunResponse"];
type SopDocumentCreateResponse = components["schemas"]["SopDocumentCreateResponse"];

/** Server-side cap (25 MB), mirrored client-side for a friendly early error. */
export const MAX_SOP_DOCUMENT_BYTES = 25_000_000;

/** Content types the ticket endpoint accepts (PDF, Word, plain text). */
export const ALLOWED_SOP_CONTENT_TYPES: readonly string[] = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "text/plain",
];

const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  doc: "application/msword",
  txt: "text/plain",
};

/**
 * Resolve the content type for a picked file. Browsers usually populate
 * `file.type`, but some leave it blank (notably for .txt/.doc from network
 * shares) — fall back to the extension so the ticket request stays honest.
 */
export function sopContentType(file: { name: string; type?: string }): string | null {
  if (file.type && ALLOWED_SOP_CONTENT_TYPES.includes(file.type)) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return EXTENSION_CONTENT_TYPES[ext] ?? null;
}

export function useSopDocuments() {
  const deptId = useAppStore((s) => s.selectedDepartmentId);
  return useQuery({
    queryKey: ["sop-documents", deptId],
    queryFn: () => api.get<SopDocument[]>("/sop/documents"),
  });
}

/** A rule is pending review while the machine wrote it (or a reviewer edited it). */
export function isPendingRule(rule: Pick<SopRule, "status">): boolean {
  return rule.status === "extracted" || rule.status === "edited";
}

export function useSopRules(documentId?: string) {
  const deptId = useAppStore((s) => s.selectedDepartmentId);
  return useQuery({
    queryKey: ["sop-rules", deptId, documentId ?? "all"],
    queryFn: () =>
      api.get<SopRule[]>("/sop/rules", {
        query: documentId ? { document_id: documentId } : undefined,
      }),
  });
}

/**
 * Three-phase upload: ticket → raw PUT to S3 → confirm. Fails loudly at the
 * phase that broke so the inline error names the actual problem.
 */
export function useUploadSopDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<SopDocument> => {
      const contentType = sopContentType(file);
      if (!contentType) {
        throw new Error("Unsupported file type — upload a PDF, Word document, or plain text.");
      }
      if (file.size < 1 || file.size > MAX_SOP_DOCUMENT_BYTES) {
        throw new Error("File must be between 1 byte and 25 MB.");
      }

      const ticket = await api.post<SopDocumentCreateResponse>("/sop/documents", {
        filename: file.name,
        content_type: contentType,
        size_bytes: file.size,
      });

      // RAW fetch: the presigned URL is its own authorization. Sending the API
      // client's Authorization / X-RespAi headers would break the S3 signature.
      const put = await fetch(ticket.upload_url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": contentType },
      });
      if (!put.ok) {
        throw new Error(`Upload to storage failed (HTTP ${put.status}).`);
      }

      return api.post<SopDocument>(`/sop/documents/${ticket.document.id}/confirm`);
    },
    onSuccess: (_doc, file) => {
      qc.invalidateQueries({ queryKey: ["sop-documents"] });
      // No filename in analytics — it could identify a department's internal docs.
      trackEvent(AnalyticsEvent.SopDocumentUploaded, {
        size_kb: Math.round(file.size / 1024),
      });
    },
  });
}

/** Synchronous server-side extraction; the response carries the upsert counts. */
export function useExtractSopDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      api.post<SopExtractionRun>(`/sop/documents/${documentId}/extract`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sop-documents"] });
      qc.invalidateQueries({ queryKey: ["sop-rules"] });
    },
  });
}

function invalidateRuleConsumers(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["sop-rules"] });
  // Approvals/rejections change what the compliance report scores.
  qc.invalidateQueries({ queryKey: ["sop-compliance"] });
}

export function useEditSopRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleId, edits }: { ruleId: string; edits: SopRuleUpdate }) =>
      api.patch<SopRule>(`/sop/rules/${ruleId}`, edits),
    onSuccess: () => invalidateRuleConsumers(qc),
  });
}

export function useApproveSopRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rule: Pick<SopRule, "id" | "category">) =>
      api.post<SopRule>(`/sop/rules/${rule.id}/approve`),
    onSuccess: (_rule, variables) => {
      invalidateRuleConsumers(qc);
      // Category only — rule/document UUIDs never reach analytics.
      trackEvent(AnalyticsEvent.SopRuleApproved, { category: variables.category });
    },
  });
}

export function useRejectSopRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      rule,
      review_note,
    }: {
      rule: Pick<SopRule, "id" | "category">;
      review_note?: string;
    }) =>
      api.post<SopRule>(`/sop/rules/${rule.id}/reject`, {
        review_note: review_note || null,
      }),
    onSuccess: (_rule, variables) => {
      invalidateRuleConsumers(qc);
      trackEvent(AnalyticsEvent.SopRuleRejected, { category: variables.rule.category });
    },
  });
}
